import { makeAutoObservable, runInAction } from 'mobx'
import api from '../services/api'
import type {
  HPCCluster,
  HPCClusterCreate,
  HPCClusterUpdate,
  HPCJob,
  HPCJobCreate,
  HPCJobUpdate,
  HPCJobSummary,
  HPCJobList,
  HPCCheckpoint,
  HPCArtifact,
  ClusterHealthResponse,
  ClusterQueueInfo,
  HPCJobMetrics,
  HPCJobStatus,
} from '../types'

class HPCStore {
  // Cluster state
  clusters: HPCCluster[] = []
  selectedCluster: HPCCluster | null = null
  clusterLoading = false
  clusterError: string | null = null

  // Job state
  jobs: HPCJobSummary[] = []
  selectedJob: HPCJob | null = null
  jobLoading = false
  jobError: string | null = null
  jobsTotal = 0
  jobsPage = 1
  jobsPageSize = 20
  jobsPages = 1

  // Checkpoint state
  checkpoints: HPCCheckpoint[] = []
  checkpointsLoading = false

  // Artifact state
  artifacts: HPCArtifact[] = []
  artifactsLoading = false

  // Real-time metrics
  currentMetrics: HPCJobMetrics | null = null

  // Queue info
  queueInfo: ClusterQueueInfo[] = []

  constructor() {
    makeAutoObservable(this)
  }

  // ==================== Cluster Actions ====================

  async fetchClusters() {
    this.clusterLoading = true
    this.clusterError = null
    try {
      const data = await api.hpcGet<{ items: HPCCluster[]; total: number }>('/clusters')
      runInAction(() => {
        this.clusters = data.items
        this.clusterLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.clusterError = error.response?.data?.detail || 'Failed to fetch clusters'
        this.clusterLoading = false
      })
    }
  }

  async fetchCluster(clusterId: string) {
    this.clusterLoading = true
    this.clusterError = null
    try {
      const data = await api.hpcGet<HPCCluster>(`/clusters/${clusterId}`)
      runInAction(() => {
        this.selectedCluster = data
        this.clusterLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.clusterError = error.response?.data?.detail || 'Failed to fetch cluster'
        this.clusterLoading = false
      })
    }
  }

  async createCluster(data: HPCClusterCreate) {
    this.clusterLoading = true
    this.clusterError = null
    try {
      const result = await api.hpcPost<HPCCluster>('/clusters', data)
      runInAction(() => {
        this.clusters.push(result)
        this.selectedCluster = result
        this.clusterLoading = false
      })
      return result
    } catch (error: any) {
      runInAction(() => {
        this.clusterError = error.response?.data?.detail || 'Failed to create cluster'
        this.clusterLoading = false
      })
      throw error
    }
  }

  async updateCluster(clusterId: string, data: HPCClusterUpdate) {
    this.clusterLoading = true
    this.clusterError = null
    try {
      const result = await api.hpcPatch<HPCCluster>(`/clusters/${clusterId}`, data)
      runInAction(() => {
        const index = this.clusters.findIndex(c => c.id === clusterId)
        if (index !== -1) {
          this.clusters[index] = result
        }
        if (this.selectedCluster?.id === clusterId) {
          this.selectedCluster = result
        }
        this.clusterLoading = false
      })
      return result
    } catch (error: any) {
      runInAction(() => {
        this.clusterError = error.response?.data?.detail || 'Failed to update cluster'
        this.clusterLoading = false
      })
      throw error
    }
  }

  async deleteCluster(clusterId: string) {
    this.clusterLoading = true
    this.clusterError = null
    try {
      await api.hpcDelete(`/clusters/${clusterId}`)
      runInAction(() => {
        this.clusters = this.clusters.filter(c => c.id !== clusterId)
        if (this.selectedCluster?.id === clusterId) {
          this.selectedCluster = null
        }
        this.clusterLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.clusterError = error.response?.data?.detail || 'Failed to delete cluster'
        this.clusterLoading = false
      })
      throw error
    }
  }

  async testClusterConnection(clusterId: string): Promise<ClusterHealthResponse> {
    try {
      const data = await api.hpcPost<ClusterHealthResponse>(`/clusters/${clusterId}/test`)
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to test connection')
    }
  }

  async fetchClusterQueues(clusterId: string) {
    try {
      const data = await api.hpcGet<ClusterQueueInfo[]>(`/clusters/${clusterId}/queues`)
      runInAction(() => {
        this.queueInfo = data
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch queue info')
    }
  }

  // ==================== Job Actions ====================

  async fetchJobs(params?: {
    cluster_id?: string
    status?: HPCJobStatus
    page?: number
    page_size?: number
  }) {
    this.jobLoading = true
    this.jobError = null
    try {
      const data = await api.hpcGet<HPCJobList>('/jobs', params)
      runInAction(() => {
        this.jobs = data.items
        this.jobsTotal = data.total
        this.jobsPage = data.page
        this.jobsPageSize = data.page_size
        this.jobsPages = data.pages
        this.jobLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.jobError = error.response?.data?.detail || 'Failed to fetch jobs'
        this.jobLoading = false
      })
    }
  }

  async fetchJob(jobId: string) {
    this.jobLoading = true
    this.jobError = null
    try {
      const data = await api.hpcGet<HPCJob>(`/jobs/${jobId}`)
      runInAction(() => {
        this.selectedJob = data
        this.jobLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.jobError = error.response?.data?.detail || 'Failed to fetch job'
        this.jobLoading = false
      })
      throw error
    }
  }

  async createJob(data: HPCJobCreate) {
    this.jobLoading = true
    this.jobError = null
    try {
      const result = await api.hpcPost<HPCJob>('/jobs', data)
      runInAction(() => {
        this.selectedJob = result
        this.jobLoading = false
      })
      return result
    } catch (error: any) {
      runInAction(() => {
        this.jobError = error.response?.data?.detail || 'Failed to create job'
        this.jobLoading = false
      })
      throw error
    }
  }

  async updateJob(jobId: string, data: HPCJobUpdate) {
    this.jobLoading = true
    this.jobError = null
    try {
      const result = await api.hpcPatch<HPCJob>(`/jobs/${jobId}`, data)
      runInAction(() => {
        this.selectedJob = result
        this.jobLoading = false
      })
      return result
    } catch (error: any) {
      runInAction(() => {
        this.jobError = error.response?.data?.detail || 'Failed to update job'
        this.jobLoading = false
      })
      throw error
    }
  }

  async deleteJob(jobId: string) {
    this.jobLoading = true
    this.jobError = null
    try {
      await api.hpcDelete(`/jobs/${jobId}`)
      runInAction(() => {
        this.jobs = this.jobs.filter(j => j.id !== jobId)
        if (this.selectedJob?.id === jobId) {
          this.selectedJob = null
        }
        this.jobLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.jobError = error.response?.data?.detail || 'Failed to delete job'
        this.jobLoading = false
      })
      throw error
    }
  }

  // Job control actions
  async submitJob(jobId: string) {
    try {
      const data = await api.hpcPost<HPCJob>(`/jobs/${jobId}/submit`)
      runInAction(() => {
        this.selectedJob = data
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to submit job')
    }
  }

  async pauseJob(jobId: string) {
    try {
      const data = await api.hpcPost<HPCJob>(`/jobs/${jobId}/pause`)
      runInAction(() => {
        this.selectedJob = data
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to pause job')
    }
  }

  async resumeJob(jobId: string) {
    try {
      const data = await api.hpcPost<HPCJob>(`/jobs/${jobId}/resume`)
      runInAction(() => {
        this.selectedJob = data
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to resume job')
    }
  }

  async cancelJob(jobId: string) {
    try {
      const data = await api.hpcPost<HPCJob>(`/jobs/${jobId}/cancel`)
      runInAction(() => {
        this.selectedJob = data
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to cancel job')
    }
  }

  async updateJobParameters(jobId: string, parameters: Record<string, any>) {
    try {
      const data = await api.hpcPost<HPCJob>(`/jobs/${jobId}/parameters`, { parameters })
      runInAction(() => {
        this.selectedJob = data
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to update parameters')
    }
  }

  async fetchJobMetrics(jobId: string) {
    try {
      const data = await api.hpcGet<HPCJobMetrics>(`/jobs/${jobId}/metrics`)
      runInAction(() => {
        this.currentMetrics = data
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch metrics')
    }
  }

  // ==================== Checkpoint Actions ====================

  async fetchCheckpoints(jobId: string) {
    this.checkpointsLoading = true
    try {
      const data = await api.hpcGet<HPCCheckpoint[]>(`/jobs/${jobId}/checkpoints`)
      runInAction(() => {
        this.checkpoints = data
        this.checkpointsLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.checkpointsLoading = false
      })
      throw new Error(error.response?.data?.detail || 'Failed to fetch checkpoints')
    }
  }

  async triggerCheckpoint(jobId: string, checkpointName?: string) {
    try {
      const data = await api.hpcPost<HPCCheckpoint>(`/jobs/${jobId}/checkpoint`, {
        checkpoint_name: checkpointName,
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to trigger checkpoint')
    }
  }

  async resumeFromCheckpoint(jobId: string, checkpointId: string) {
    try {
      const data = await api.hpcPost<HPCJob>(`/jobs/${jobId}/resume-from-checkpoint`, {
        checkpoint_id: checkpointId,
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to resume from checkpoint')
    }
  }

  // ==================== Artifact Actions ====================

  async fetchArtifacts(jobId: string) {
    this.artifactsLoading = true
    try {
      const data = await api.hpcGet<HPCArtifact[]>(`/jobs/${jobId}/artifacts`)
      runInAction(() => {
        this.artifacts = data
        this.artifactsLoading = false
      })
      return data
    } catch (error: any) {
      runInAction(() => {
        this.artifactsLoading = false
      })
      throw new Error(error.response?.data?.detail || 'Failed to fetch artifacts')
    }
  }

  async scanArtifacts(jobId: string) {
    try {
      const data = await api.hpcPost<HPCArtifact[]>(`/jobs/${jobId}/scan-artifacts`)
      runInAction(() => {
        this.artifacts = [...this.artifacts, ...data]
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to scan artifacts')
    }
  }

  async downloadArtifact(artifactId: string, localPath?: string) {
    try {
      const data = await api.hpcPost<HPCArtifact>(`/artifacts/${artifactId}/download`, {
        local_path: localPath,
      })
      runInAction(() => {
        const index = this.artifacts.findIndex(a => a.id === artifactId)
        if (index !== -1) {
          this.artifacts[index] = data
        }
      })
      return data
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Failed to download artifact')
    }
  }

  // ==================== WebSocket Event Handlers ====================

  handleJobUpdate(event: any) {
    runInAction(() => {
      // Update job in list
      const jobIndex = this.jobs.findIndex(j => j.id === event.job_id)
      if (jobIndex !== -1) {
        this.jobs[jobIndex] = {
          ...this.jobs[jobIndex],
          status: event.status,
          progress_percent: event.progress_percent,
        }
      }

      // Update selected job
      if (this.selectedJob && this.selectedJob.id === event.job_id) {
        this.selectedJob = {
          ...this.selectedJob,
          id: this.selectedJob.id,
          status: event.status,
          progress_percent: event.progress_percent,
          current_epoch: event.current_epoch,
          current_step: event.current_step,
          pbs_job_id: event.pbs_job_id,
          queue_position: event.queue_position,
          estimated_wait_minutes: event.estimated_wait_minutes,
          error_message: event.error_message,
        }
      }
    })
  }

  handleMetricsUpdate(event: any) {
    runInAction(() => {
      if (this.selectedJob && this.selectedJob.id === event.job_id) {
        this.currentMetrics = {
          job_id: event.job_id,
          timestamp: event.timestamp,
          progress_percent: event.progress_percent,
          current_epoch: event.current_epoch,
          current_step: event.current_step,
          metrics: event.metrics,
          resource_usage: event.resource_usage,
        }

        this.selectedJob = {
          ...this.selectedJob,
          id: this.selectedJob.id,
          progress_percent: event.progress_percent,
          current_epoch: event.current_epoch,
          current_step: event.current_step,
          metrics: event.metrics,
          resource_usage: event.resource_usage,
        }
      }
    })
  }

  handleCheckpointCreated(event: any) {
    runInAction(() => {
      if (this.selectedJob?.id === event.job_id) {
        // Refresh checkpoints list
        this.fetchCheckpoints(event.job_id)
      }
    })
  }

  handleArtifactsFound(event: any) {
    runInAction(() => {
      if (this.selectedJob?.id === event.job_id) {
        // Refresh artifacts list
        this.fetchArtifacts(event.job_id)
      }
    })
  }

  handleClusterStatusUpdate(event: any) {
    runInAction(() => {
      const clusterIndex = this.clusters.findIndex(c => c.id === event.cluster_id)
      if (clusterIndex !== -1) {
        this.clusters[clusterIndex] = {
          ...this.clusters[clusterIndex],
          status: event.status,
          last_health_check: event.last_health_check,
          health_check_message: event.health_check_message,
        }
      }

      if (this.selectedCluster && this.selectedCluster.id === event.cluster_id) {
        this.selectedCluster = {
          ...this.selectedCluster,
          id: this.selectedCluster.id,
          status: event.status,
          last_health_check: event.last_health_check,
          health_check_message: event.health_check_message,
        }
      }
    })
  }

  // ==================== Utility Methods ====================

  clearSelectedJob() {
    this.selectedJob = null
    this.checkpoints = []
    this.artifacts = []
    this.currentMetrics = null
  }

  clearSelectedCluster() {
    this.selectedCluster = null
    this.queueInfo = []
  }

  clearErrors() {
    this.clusterError = null
    this.jobError = null
  }
}

export const hpcStore = new HPCStore()
export default hpcStore
